import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { ExperienceDetails } from '@atlassian/jira-business-experience-tracking/src/types.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useFlagService } from '@atlassian/jira-flags/src/services'; // ignore-for-ENGHEALTH-17759
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	DeleteJwmCategory,
	DeleteJwmCategoryVariables,
} from './__generated_apollo__/DeleteJwmCategory';
import {
	DELETE_FAILED_FLAG,
	DELETE_SUCCESS_FLAG,
	PACKAGE_LOCATION,
	QUERY_NAME,
} from './constants.tsx';
import { DELETE_JIRA_BUSINESS_CATEGORY } from './gql.tsx';
import type { DeleteCategoryProps } from './types.tsx';

export type UseDeleteCategoryProps = {
	experience: ExperienceDetails;
	onCompleted?: (result: DeleteJwmCategory) => void;
};
export const useDeleteCategory = ({ experience, onCompleted }: UseDeleteCategoryProps) => {
	const startExperience = useExperienceStart(experience);
	const markExperienceSuccess = useExperienceSuccess(experience);
	const markExperienceFailed = useExperienceFail(experience);

	const { showFlag } = useFlagService();
	const project = useProject();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectId = String(project.id);

	const [deleteJwmCategory, { data, loading, error }] = useMutation<
		DeleteJwmCategory,
		DeleteJwmCategoryVariables
	>(DELETE_JIRA_BUSINESS_CATEGORY, {
		onError: (apolloError) => {
			markExperienceFailed(`${experience.experience} category service error handling`, apolloError);
			showFlag(DELETE_FAILED_FLAG);

			log.safeErrorWithoutCustomerData(PACKAGE_LOCATION, 'ApolloError: Failed to delete category');
		},
		onCompleted: (onCompletedData) => {
			markExperienceSuccess();
			if (onCompleted) {
				onCompleted(onCompletedData);
			}
			if (onCompletedData?.deleteJwmCategory?.errors) {
				showFlag(DELETE_FAILED_FLAG);
				return;
			}
			showFlag(DELETE_SUCCESS_FLAG);
			fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`);
		},
	});

	const deleteCategory = useCallback(
		async ({ issueTypeId, optionId }: DeleteCategoryProps): Promise<boolean> => {
			startExperience();

			const response = await deleteJwmCategory({
				variables: {
					input: {
						projectId,
						issueTypeId,
						optionId,
					},
				},
			});

			const result = response?.data?.deleteJwmCategory;

			if (result?.success) {
				return true;
			}

			return false;
		},
		[deleteJwmCategory, projectId, startExperience],
	);

	// find error that has message
	const mutationBadRequestError = useMemo(
		() => data?.deleteJwmCategory?.errors?.find((e) => e.message),
		[data],
	);

	return {
		error:
			// if there are any errors from Apollo, surface that first
			// otherwise if there are errors from bad request, use that
			error || mutationBadRequestError,
		data,
		loading,
		deleteCategory,
	};
};
